import type { GscSourceStatus } from './api/gscConnectionApi';

export type SelectableProperty = {
  propertyUrl: string;
  isDisconnected: boolean;
  // Representative source per type, kept for the option label and the
  // `isDisconnected` reading — not for querying: no visibility read endpoint
  // filters by source id, so widgets scope by `propertyUrl` instead.
  gsc?: GscSourceStatus;
  prerender?: GscSourceStatus;
};

// The kept source is the one whose data the dashboard can actually show: a live
// connection wins, and among equally-live ones the most recently synced. Sync
// timestamps are UTC ISO strings from the same API, so they order lexically.
function representsMoreRecentData(candidate: GscSourceStatus, kept: GscSourceStatus): boolean {
  const candidateIsLive = candidate.status === 'ACTIVE';
  const keptIsLive = kept.status === 'ACTIVE';
  if (candidateIsLive !== keptIsLive) return candidateIsLive;
  return (candidate.lastSyncAt ?? '') > (kept.lastSyncAt ?? '');
}

function hasDataOrIsLive(source?: GscSourceStatus): boolean {
  return source !== undefined && (source.status === 'ACTIVE' || source.lastSyncAt !== null);
}

// A property's data is the join of its GSC source and its companion PRERENDER
// source, which the visibility API models as independent (their statuses can
// diverge: disconnect is per-source, and Prerender keeps syncing after a GSC
// disconnect). Group both types by propertyUrl, keep a representative source
// per type (reconnect cycles leave duplicates), and call the property
// selectable when any half has synced at least once or is live.
const GSC_STATUS_RANK: Record<string, number> = { ACTIVE: 0, ERROR: 1 };

function gscStatusRank(property: SelectableProperty): number {
  return GSC_STATUS_RANK[property.gsc?.status ?? ''] ?? 2;
}

/**
 * The property the dashboard reports on when the URL names none: a live GSC
 * connection first, a broken one next, anything else last — a disconnected
 * property shows only its own history (CEN-284), so it must never be the
 * landing state while a live one exists. Stable within a tier.
 */
export function defaultSelectableProperty(properties: SelectableProperty[]): SelectableProperty | undefined {
  return properties.reduce<SelectableProperty | undefined>(
    (best, candidate) => (best === undefined || gscStatusRank(candidate) < gscStatusRank(best) ? candidate : best),
    undefined
  );
}

export function resolveSelectedProperty(
  properties: SelectableProperty[],
  requestedPropertyUrl: string | undefined
): SelectableProperty | undefined {
  return (
    properties.find((property) => property.propertyUrl === requestedPropertyUrl) ??
    defaultSelectableProperty(properties)
  );
}

export function toSelectableProperties(sources: GscSourceStatus[]): SelectableProperty[] {
  const byPropertyUrl = new Map<string, { gsc?: GscSourceStatus; prerender?: GscSourceStatus }>();
  for (const source of sources) {
    if (source.propertyUrl === null) continue;
    const typeKey = source.type === 'GSC' ? 'gsc' : 'prerender';
    const group = byPropertyUrl.get(source.propertyUrl) ?? {};
    const kept = group[typeKey];
    if (!kept || representsMoreRecentData(source, kept)) group[typeKey] = source;
    byPropertyUrl.set(source.propertyUrl, group);
  }
  return Array.from(byPropertyUrl.entries())
    .filter(([, group]) => hasDataOrIsLive(group.gsc) || hasDataOrIsLive(group.prerender))
    .map(([propertyUrl, group]) => ({
      propertyUrl,
      isDisconnected: group.gsc?.status === 'DISCONNECTED',
      gsc: group.gsc,
      prerender: group.prerender,
    }));
}
